import { Channel } from 'api/ChannelsAPI';
import { MessageType } from 'api/MessageTypeAPI';
import { EspAccount } from 'modules/graphql/types';
import { useModalContext } from 'utils/contexts/modal';

export enum CHANNEL_CONFIGURATION_ACTION {
  CREATE_CHANNEL = 'create-channel',
  CREATE_MESSAGE_TYPE = 'create-message-type',
  DELETE_CHANNEL = 'delete-channel',
  DELETE_MESSAGE_TYPE = 'delete-message-type',
  EDIT_CHANNEL = 'edit-channel',
  EDIT_MESSAGE_TYPE = 'edit-message-type',
  IGNORE_FREQUENCY_CAP = 'ignore-frequency-cap',
  UPDATE_FREQUENCY_CAP = 'update-frequency-cap',
  UPDATE_RATE_LIMIT = 'update-rate-limit',
  UPDATE_TO_DOUBLE_OPT_IN = 'update-to-double-opt-in',
}

export interface ModalState {
  action: CHANNEL_CONFIGURATION_ACTION;
  channel?: Channel;
  channelId?: MessageType['messageChannelId'];
  messageType?: MessageType;
  espAccounts?: EspAccount[];
}

export type ModalContextState = {
  showModal: (modalState: ModalState) => void;
  hideModal: () => void;
  modalState: ModalState | null;
};

export const useChannelModalsContext = () => {
  const { showModal, hideModal, modalState } = useModalContext<ModalState>();

  const showCreateMessageTypeModal = (channel: Channel) =>
    showModal({
      action: CHANNEL_CONFIGURATION_ACTION.CREATE_MESSAGE_TYPE,
      channel,
    });

  const showUpdateMessageTypeModal = (messageType: MessageType) =>
    showModal({
      action: CHANNEL_CONFIGURATION_ACTION.EDIT_MESSAGE_TYPE,
      messageType,
    });

  const showCreateChannelModal = () =>
    showModal({
      action: CHANNEL_CONFIGURATION_ACTION.CREATE_CHANNEL,
    });

  const showUpdateChannelModal = (channel: Channel, espAccounts: EspAccount[]) =>
    showModal({
      action: CHANNEL_CONFIGURATION_ACTION.EDIT_CHANNEL,
      channel,
      espAccounts,
    });

  const showDeleteChannelModal = (channel: Channel) =>
    showModal({
      action: CHANNEL_CONFIGURATION_ACTION.DELETE_CHANNEL,
      channel,
    });

  const showDeleteMessageTypeModal = (messageType: MessageType) =>
    showModal({
      action: CHANNEL_CONFIGURATION_ACTION.DELETE_MESSAGE_TYPE,
      messageType,
    });

  const showUpdateFrequencyCapModal = (messageType: MessageType) =>
    showModal({
      action: CHANNEL_CONFIGURATION_ACTION.UPDATE_FREQUENCY_CAP,
      messageType,
    });

  const showUpdateRateLimitModal = (messageType: MessageType) =>
    showModal({
      action: CHANNEL_CONFIGURATION_ACTION.UPDATE_RATE_LIMIT,
      messageType,
    });

  const showIgnoreFrequencyManagement = (messageType: MessageType) =>
    showModal({ action: CHANNEL_CONFIGURATION_ACTION.IGNORE_FREQUENCY_CAP, messageType });

  const showUpdateToDoubleOptInModal = (messageType: MessageType) =>
    showModal({ action: CHANNEL_CONFIGURATION_ACTION.UPDATE_TO_DOUBLE_OPT_IN, messageType });

  return {
    showCreateChannelModal,
    showCreateMessageTypeModal,
    showDeleteChannelModal,
    showDeleteMessageTypeModal,
    showIgnoreFrequencyManagement,
    showUpdateFrequencyCapModal,
    showUpdateRateLimitModal,
    showUpdateChannelModal,
    showUpdateMessageTypeModal,
    showUpdateToDoubleOptInModal,
    hideModal,
    modalState,
  };
};
